import type { HarmonyPackageBoard, HarmonyPackageBoardColumn } from '@rhythmic/db/types'
import type {
  CreateKanbanBoardItemPayload,
  CreateKanbanColumnPayload,
  DeleteKanbanBoardItemPayload,
  DeleteKanbanColumnPayload,
  KanbanCard,
  KanbanWorkitemMutationPayload,
  UpdateKanbanBoardItemPayload,
  UpdateKanbanColumnPayload,
  Workitem,
} from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { defineStore, storeToRefs } from 'pinia'
import { computed, onScopeDispose, readonly, ref } from 'vue'
import { useRhythmicLogger } from '#imports'
import { useReactiveDbPaneScope } from '../../composables/useReactiveDbContextRegistry'
import { getTeamTagsCollection, getWorkitemTagsCollection } from '../getCollections'
import type { HarmonyItem } from '../queries'
import useTeamContext from '../team/useTeamContext'
import createMutationHandlers from './useKanbanBoardContext.mutators'
import useKanbanBoardContextQueries from './useKanbanBoardContext.queries'
import type { UpdateKanbanBoardPayload } from './useKanbanBoardContext.updateBoard'

export const useKanbanBoardContext = defineStore('kanbanBoardContext', () => {
  const logger = useRhythmicLogger('kanbanBoardContextStore')
  const { workspaceId, harmonyPlan, team } = storeToRefs(useTeamContext())

  const queries = useKanbanBoardContextQueries()
  const resolvedTeamId = computed(() => team.value?.id ?? undefined)
  queries.init({ logger, teamId: resolvedTeamId, workspaceId })

  const selectedCard = ref<KanbanCard | null>(null)
  const selectedWorkitem = ref<Workitem | null>(null)

  const sortBy = ref<'status' | 'parent'>('status')
  const compactMode = ref(false)
  const showEpics = ref(true)
  const showChat = ref(true)
  const showNavigator = ref(true)

  const isSoftDeletedWorkitem = (item: Workitem | null | undefined): boolean => {
    if (!item) return false
    const candidate = item as Workitem & { is_deleted?: boolean | null; isDeleted?: boolean | null }
    return candidate.is_deleted === true || candidate.isDeleted === true
  }

  const _boards = computed<HarmonyPackageBoard[]>(() => queries.boards.value)
  const board = computed<HarmonyPackageBoard | undefined>(() => queries.board.value)
  const burndown = computed(() => board.value?.forecast_burndown ?? [])
  const columns = computed<HarmonyPackageBoardColumn[]>(() => queries.columns.value)
  const harmonyPackageBoardItems = computed(() => queries.harmonyPackageBoardItems.value)
  const kanbanCards = computed<KanbanCard[]>(() =>
    (harmonyPackageBoardItems.value ?? []).filter((card) => !isSoftDeletedWorkitem(card.item)),
  )
  const harmonyItems = computed<HarmonyItem[]>(() => queries.harmonyItems.value)
  const users = computed(() => [])

  const currentSelectedCard = computed(() => selectedCard.value)
  const currentSelectedWorkitem = computed(() => selectedWorkitem.value)

  const isLoading = computed(() => queries.isLoading.value)
  const isReady = computed(() => queries.isReady.value)
  const hasError = computed(() => queries.hasError.value)

  const boardCollectionStatus = computed(() => queries.boardsCollection.value?.status)
  const columnCollectionStatus = computed(() => queries.columnsCollection.value?.status)
  const boardItemsCollectionStatus = computed(() => queries.boardItemsCollection.value?.status)
  const packageItemsCollectionStatus = computed(() => queries.packageItemsCollection.value?.status)
  const boardSize = computed(() => queries.boardsCollection.value?.size)
  const columnSize = computed(() => queries.columnsCollection.value?.size)
  const boardItemsSize = computed(() => queries.boardItemsCollection.value?.size)
  const packageItemsSize = computed(() => queries.packageItemsCollection.value?.size)

  const collections = computed(() => ({
    board: {
      collection: queries.boardsCollection.value,
      status: boardCollectionStatus.value,
      size: boardSize.value,
    },
    columns: {
      collection: queries.columnsCollection.value,
      status: columnCollectionStatus.value,
      size: columnSize.value,
    },
    boardItems: {
      collection: queries.boardItemsCollection.value,
      status: boardItemsCollectionStatus.value,
      size: boardItemsSize.value,
    },
    packageItems: {
      collection: queries.packageItemsCollection.value,
      status: packageItemsCollectionStatus.value,
      size: packageItemsSize.value,
    },
  }))

  const teamTagsCollection = computed(() => {
    const teamId = team.value?.id
    if (!teamId) return undefined
    return getTeamTagsCollection(teamId)
  })

  const workitemTagsCollection = computed(() => {
    const teamId = team.value?.id
    if (!teamId) return undefined
    return getWorkitemTagsCollection(teamId)
  })

  const paneScope = useReactiveDbPaneScope()
  let paneDisposerId: string | null = null

  const clearUiState = () => {
    selectedCard.value = null
    selectedWorkitem.value = null
  }

  const dispose = () => {
    queries.reset({ logger })
    clearUiState()
  }

  if (paneScope) {
    paneDisposerId = paneScope.registerDisposer(
      async () => {
        queries.reset({ logger })
        clearUiState()
      },
      { id: 'kanban-board-context', label: 'kanban-board-context' },
    )

    onScopeDispose(() => {
      if (!paneDisposerId) return

      paneScope.releaseDisposer(paneDisposerId)
      paneDisposerId = null
    })
  }

  const currentContext = computed(() => {
    const currentBoardId = queries.boardId.value
    const packageId = harmonyPlan.value?.id ?? queries.harmonyPlanId.value
    const currentWorkspaceId = workspaceId.value
    const currentTeamId = team.value?.id

    if (!currentBoardId || !packageId || !currentWorkspaceId || !currentTeamId) return undefined

    return {
      boardId: currentBoardId,
      packageId,
      workspaceId: currentWorkspaceId,
      teamId: currentTeamId,
    }
  })

  const mutationHandlers = computed(() => {
    if (!currentContext.value) {
      return {
        createColumn: () => {
          throw new Error('Context not initialized')
        },
        updateColumn: () => {
          throw new Error('Context not initialized')
        },
        deleteColumn: () => {
          throw new Error('Context not initialized')
        },
        createBoardItem: () => {
          throw new Error('Context not initialized')
        },
        updateBoardItem: (): Transaction => {
          throw new Error('Context not initialized')
        },
        updateWorkitem: (): Transaction => {
          throw new Error('Context not initialized')
        },
        deleteBoardItem: () => {
          throw new Error('Context not initialized')
        },
        addExistingItem: (): Transaction => {
          throw new Error('Context not initialized')
        },
        updateBoard: (): Transaction => {
          throw new Error('Context not initialized')
        },
      }
    }

    return createMutationHandlers(currentContext, () => ({
      boardItems: queries.boardItemsCollection.value,
      workItems: queries.workitemsCollection.value,
      teamTags: teamTagsCollection.value,
      workitemTags: workitemTagsCollection.value,
    }))
  })

  return {
    queries,
    statuses: {
      boardQuery: queries.boardQuery.value?.status ?? 'not available',
      columnsQuery: queries.columnsQuery.value?.status ?? 'not available',
      boardItemsQuery: queries.boardItemsQuery.value?.status ?? 'not available',
      harmonyItemsQuery: queries.harmonyItemsQuery.value?.status ?? 'not available',
    },
    dispose,
    board,
    columns,
    harmonyPackageBoardItems,
    harmonyItems,
    kanbanCards,
    currentSelectedCard,
    currentSelectedWorkitem,
    burndown,
    boardId: queries.boardId,
    harmonyPlanId: queries.harmonyPlanId,
    isLoading: readonly(isLoading),
    isReady: readonly(isReady),
    hasError: readonly(hasError),
    isBoardLoading: queries.isBoardLoading,
    isColumnLoading: queries.isColumnsLoading,
    isBoardItemsLoading: queries.isBoardItemsLoading,
    isHarmonyItemsLoading: queries.isHarmonyItemsLoading,
    isBoardReady: queries.isBoardReady,
    isColumnReady: queries.isColumnsReady,
    isBoardItemsReady: queries.isBoardItemsReady,
    isHarmonyItemsReady: queries.isHarmonyItemsReady,
    hasBoardError: queries.hasBoardError,
    hasColumnError: queries.hasColumnsError,
    hasBoardItemsError: queries.hasBoardItemsError,
    hasHarmonyItemsError: queries.hasHarmonyItemsError,
    compactMode,
    sortBy,
    showChat,
    showEpics,
    showNavigator,
    users,
    collections,
    createColumn: (payload: CreateKanbanColumnPayload): Transaction => mutationHandlers.value.createColumn(payload),
    updateColumn: (payload: UpdateKanbanColumnPayload): Transaction => mutationHandlers.value.updateColumn(payload),
    deleteColumn: (payload: DeleteKanbanColumnPayload): Transaction => mutationHandlers.value.deleteColumn(payload),
    createBoardItem: (payload: CreateKanbanBoardItemPayload) => mutationHandlers.value.createBoardItem(payload),
    updateBoardItem: (payload: UpdateKanbanBoardItemPayload): Transaction =>
      mutationHandlers.value.updateBoardItem(payload),
    updateWorkitem: (payload: KanbanWorkitemMutationPayload): Transaction =>
      mutationHandlers.value.updateWorkitem(payload),
    deleteBoardItem: (payload: DeleteKanbanBoardItemPayload): Transaction =>
      mutationHandlers.value.deleteBoardItem(payload),
    addExistingItem: (payload: HarmonyItem[]): Transaction => mutationHandlers.value.addExistingItem(payload),
    updateBoard: (payload: UpdateKanbanBoardPayload): Transaction => mutationHandlers.value.updateBoard(payload),
  }
})

export type KanbanBoardContextStore = ReturnType<typeof useKanbanBoardContext>
export default useKanbanBoardContext
